import { AppContainer, ModalBox } from '@engyalo/delivery-ui-components'
import ReactGA from 'react-ga4'
import { datadogRum } from '@datadog/browser-rum'
import { Suspense, useEffect, useMemo } from 'react'
import { useLoadViews } from '../hooks/templates/useLoadViews'
import useTemplateComponentsState from '../hooks/templates/useTemplateComponentsState'
import { IModal, IModals } from '../interfaces'
import { RootState } from '../redux/store'
import { getCinnamonPropsContainer, useAppSelector, useModal } from '../hooks'
import EventEmitter from '../classes/EventEmiter'
import { generateDataDogContext } from '../utils/dataDog'
import { isObject } from '../utils/validTypes'

interface IModalContainer {
  name: string
  modal: IModal
  eventEmitter: EventEmitter
  state: RootState
  modalStatus: IModals
  containerUid?: string
}

const SECTIONS = ['header', 'body', 'footer'] as const

/**
 * Why `name` opens with nothing to render, or `null` when `config.modals`
 * authors it with at least one component.
 *
 * Read off the processed `config.modals` in the store and not off the `modal`
 * prop: the templates are handed `config.modals || {}` and turn a missing
 * entry into `{}`, so the prop cannot tell a `null` object from a name that
 * is merely absent. The test on a present entry is its components, not its
 * truthiness: an entry authored as `null` replaces the default entry, and
 * `processConfig` then merges `DEFAULT_MODALS_PROPS` under it, so for a name
 * whose props carry a `body` it reaches the store as their empty sections —
 * truthy, and as blank as a missing one. So does an entry whose components
 * are explicitly empty, since the merge replaces arrays. An entry authored as
 * `{}` is not one of these: it merges over `defaultConfig.modals` and keeps
 * the default components. The own-key check is deliberately stricter than
 * `useModalsList`'s `modals[name] || {}`, which hands an inherited name such
 * as `toString` a prototype function as its modal.
 *
 * `configModalsAbsent` is defensive: `processConfig` merges the remote config
 * over `defaultConfig`, which authors `modals`, so an omitted key keeps the
 * defaults. A remote `modals: null` is not merged, it replaces them.
 */
const unauthoredModalCause = (modals: unknown, name: string) => {
  if (modals === null) {
    return 'configModalsNull'
  }
  if (modals === undefined) {
    return 'configModalsAbsent'
  }
  if (!isObject(modals)) {
    return 'configModalsNotAnObject'
  }
  const byName = modals as Record<string, Partial<IModal> | null | undefined>
  if (!Object.hasOwn(byName, name)) {
    return 'nameNotAuthored'
  }
  const entry = byName[name]
  const hasComponents = SECTIONS.some((section) => {
    const components = entry?.[section]?.components
    return Array.isArray(components) && components.length > 0
  })
  return hasComponents ? null : 'noComponents'
}

const ModalContainer = ({
  name = '',
  modal,
  eventEmitter,
  state,
  modalStatus,
  containerUid,
}: IModalContainer) => {
  const { closeModal, performModalActions } = useModal()
  const modals = useAppSelector((state) => state.defaultSlice.modals)
  const processedModal = useMemo(
    () => getCinnamonPropsContainer(modal, state, eventEmitter),
    [modal, state, eventEmitter],
  )
  const {
    uid: modalUid,
    header = { components: [] },
    body = { components: [] },
    footer = { components: [] },
    props = {},
  } = modal

  /*
   * A listed name can have no status entry: the slice is built from the keys
   * of `config.modals` only, so a misspelled name, or a tenant whose
   * top-level `modals` is `null` beside a default container list, lands here. That used to throw on the destructuring (COM-1337); now
   * the modal mounts closed, like an authored one with `show: false`. It is
   * not filtered out of the list, because the entry is not final: `openModal`
   * writes one for any name, and the modal opens once it does.
   */
  const show = !!modalStatus[name]?.show
  const { props: headerProps, components: headerComponents } = header
  const { props: bodyProps, components: bodyComponents } = body
  const { props: footerProps, components: footerComponents } = footer
  useEffect(() => {
    if (show) {
      ReactGA.send({ hitType: 'pageview', page: `/${name}` })
      window.fbq('track', 'PageView', { page: `/${name}` })
    }
  }, [name, show])

  /*
   * A listed name `config.modals` gives nothing to render still opens, as a
   * blank overlay the shopper can only close. That is left as it is; this makes it
   * visible. Keyed on `show`, so it reports once per opening and again on a
   * reopen, not on every render while open — and unlatched, like the other
   * config reports, since a duplicate groups away and a missing one does not.
   */
  const configModals = state.defaultSlice.config?.modals
  const unauthoredCause = useMemo(
    () => unauthoredModalCause(configModals, name),
    [configModals, name],
  )
  useEffect(() => {
    if (!show || !unauthoredCause) return
    datadogRum.addError(
      new Error(
        `modal ${JSON.stringify(
          name,
        )} opened with no authored components (${unauthoredCause}), so it shows a blank overlay`,
      ),
      generateDataDogContext({
        title: 'modal opened with no authored components',
        extraInfo: {
          function: 'ModalContainer',
          modalName: name,
          cause: unauthoredCause,
          containerUid,
        },
      }),
    )
  }, [name, show, unauthoredCause, containerUid])

  const {
    headerComponentsList,
    setHeaderComponentsList,
    bodyComponentsList,
    setBodyComponentsList,
    footerComponentsList,
    setFooterComponentsList,
  } = useTemplateComponentsState()

  useLoadViews({
    headerComponents,
    setHeaderComponentsList,
    bodyComponents,
    setBodyComponentsList,
    footerComponents,
    setFooterComponentsList,
    containerUid: modalUid,
  })

  const closeActions = () => {
    closeModal({ container: name, state })
    performModalActions({ container: name, modalStatus: !!modals?.[name]?.show })
  }

  return (
    <Suspense fallback={null}>
      <ModalBox
        id={modalUid}
        open={show}
        closeActions={closeActions}
        unmonuntOnClose
        styles={{
          zIndex: 1101,
        }}
        {...props}>
        <AppContainer
          modal
          header={{
            ...headerProps,
            id: `${modalUid}-header`,
            components: headerComponentsList.map(
              ({ uid, wrapper: Wrapper }) => (
                <Wrapper
                  parentUid={modalUid}
                  key={uid}
                  containerSection={processedModal.header}
                />
              ),
            ),
          }}
          body={{
            ...bodyProps,
            id: `${modalUid}-body`,
            components: bodyComponentsList.map(({ uid, wrapper: Wrapper }) => (
              <Wrapper
                parentUid={modalUid}
                key={uid}
                containerSection={processedModal.body}
              />
            )),
          }}
          footer={{
            ...footerProps,
            id: `${modalUid}-footer`,
            components: footerComponentsList.map(
              ({ uid, wrapper: Wrapper }) => (
                <Wrapper
                  parentUid={modalUid}
                  key={uid}
                  containerSection={processedModal.footer}
                />
              ),
            ),
          }}
        />
      </ModalBox>
    </Suspense>
  )
}

export default ModalContainer
